import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { t } from "i18next";
import { showFallbackErrorToast } from "@apis/http/httpErrorToast";
import {
  createUserProduct,
  fetchAiAnalyzeImage,
  fetchParseInfoByLink,
  fetchProductSellingPointResult,
  fetchUserProductDetail,
  generateProductSellingPoints,
  getInferSellingPoints,
  updateUserProduct,
} from "@apis/http/api";
import {
  ProductInfoByLinkResponseLinkSource,
  UserProductCategorySource,
  UserProductImageSource,
  UserProductSellingPointSource,
  UserProductUpsertRequestSourceType,
} from "@apis/generated/openapi";
import { uploadProductImage } from "@apis/upload";
import type { ClipUploadContext, ClipUploadFile } from "@components/ClipUpload/ClipUpload";
import {
  IMAGE_CAPTION_MAX_LENGTH,
  MAX_PRODUCT_IMAGES,
  MAX_SELLING_POINTS,
  type ProductFormState,
  type ProductImageFile,
  type ProductImageResponse,
  type SellingPointItem,
} from "./types";
import { useProductFormDirty } from "./useProductFormDirty";

const SELLING_POINTS_POLL_INTERVAL = 5000;

let slotIdSeq = 0;
const nextSlotUid = () => `img-slot-${++slotIdSeq}-${Date.now()}`;
let spIdSeq = 0;
const nextSpId = () => `sp-${++spIdSeq}`;

const linkSourceToImageSource: Record<ProductInfoByLinkResponseLinkSource, UserProductImageSource> = {
  [ProductInfoByLinkResponseLinkSource.KALODATA]: UserProductImageSource.KALODATA,
  [ProductInfoByLinkResponseLinkSource.TIKTOK]: UserProductImageSource.TIKTOK,
  [ProductInfoByLinkResponseLinkSource.AMAZON]: UserProductImageSource.AMAZON,
};

const linkSourceToUpsertSource: Record<ProductInfoByLinkResponseLinkSource, UserProductUpsertRequestSourceType> = {
  [ProductInfoByLinkResponseLinkSource.KALODATA]: UserProductUpsertRequestSourceType.KALODATA,
  [ProductInfoByLinkResponseLinkSource.TIKTOK]: UserProductUpsertRequestSourceType.TIKTOK,
  [ProductInfoByLinkResponseLinkSource.AMAZON]: UserProductUpsertRequestSourceType.AMAZON,
};

const emptyForm = (): ProductFormState => ({
  sourceType: UserProductUpsertRequestSourceType.MANUAL,
  title: "",
  category: "",
  sellingPoints: [],
});

const getMeta = (file: ClipUploadFile): ProductImageResponse => {
  const raw = file.response as ProductImageResponse | undefined;
  return {
    assetId: raw?.assetId,
    description: raw?.description ?? "",
    aiAnalyzed: !!raw?.aiAnalyzed,
    source: raw?.source ?? UserProductImageSource.USER,
  };
};

/** 把后端商品详情结构反序列化为表单 + 图片列表 */
const detailToForm = (detail: any): { form: ProductFormState; imageFiles: ProductImageFile[] } => {
  // mainImage 的 url 可能也出现在 images 里，需去重，否则编辑态会多渲染一张重复图片
  const seenUrls = new Set<string>();
  const allImages: any[] = [
    ...(detail.mainImage ? [detail.mainImage] : []),
    ...(Array.isArray(detail.images) ? detail.images : []),
  ].filter((img) => {
    const url = img?.url;
    if (!url || seenUrls.has(url)) return false;
    seenUrls.add(url);
    return true;
  });
  const captions = (detail?.metadata?.imageCaptions as string[]) ?? [];

  const imageFiles: ProductImageFile[] = allImages.slice(0, MAX_PRODUCT_IMAGES).map((img, index) => ({
    uid: nextSlotUid(),
    name: `image-${index}`,
    url: img?.url ?? "",
    response: {
      assetId: img?.assetId ?? undefined,
      description: captions[index]?.slice(0, IMAGE_CAPTION_MAX_LENGTH) ?? "",
      // 详情里的图片可以认为已经分析过（来自服务器）
      aiAnalyzed: true,
      source: (img?.source as UserProductImageSource) ?? UserProductImageSource.USER,
    } satisfies ProductImageResponse,
  }));

  const cat = detail?.category ?? {};
  const categoryText = [cat.priCateName, cat.secCateName, cat.terCateName].filter(Boolean).join(" > ");

  const sellingPoints: SellingPointItem[] = (detail?.sellingPoints ?? []).map((sp: any) => ({
    id: nextSpId(),
    content: sp.content ?? "",
    source: sp.source,
    checked: true,
  }));

  return {
    form: {
      sourceType:
        (detail?.sourceType as UserProductUpsertRequestSourceType) ?? UserProductUpsertRequestSourceType.MANUAL,
      sourceProductId: detail?.sourceProductId,
      sourceUrl: detail?.sourceUrl,
      country: detail?.country,
      title: detail?.title ?? "",
      category: categoryText,
      categorySource: cat.source,
      sellingPoints,
    },
    imageFiles,
  };
};

interface UseProductFormParams {
  productId?: number | null;
  enabled: boolean;
  onSuccess?: (detail: UserProductDetail | null) => void;
}

/** createUserProduct / updateUserProduct 返回的商品 detail（schema = UserProductResponse） */
export type UserProductDetail = any;

export type SellingPointsStatus = "idle" | "loading" | "succeeded" | "failed";

export const useProductForm = ({ productId, enabled, onSuccess }: UseProductFormParams) => {
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [imageFiles, setImageFiles] = useState<ProductImageFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [parsingLink, setParsingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [sellingPointsStatus, setSellingPointsStatus] = useState<SellingPointsStatus>("idle");
  const [aiAnalyzing, setAiAnalyzing] = useState(false);

  const sellingPointsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sellingPointsSeqRef = useRef(0);
  const formRef = useRef(form);
  formRef.current = form;
  const imageFilesRef = useRef(imageFiles);
  imageFilesRef.current = imageFiles;

  const { isDirty, captureBaseline, resetBaseline } = useProductFormDirty(form, imageFiles, formRef, imageFilesRef);

  const clearSellingPointsTimer = useCallback(() => {
    if (sellingPointsTimerRef.current) {
      clearInterval(sellingPointsTimerRef.current);
      sellingPointsTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearSellingPointsTimer(), [clearSellingPointsTimer]);

  // 编辑态：拉详情
  useEffect(() => {
    if (!enabled || !productId) return;
    let cancelled = false;
    setLoading(true);
    fetchUserProductDetail({ productId } as any)
      .then((detail: any) => {
        if (cancelled || !detail) return;
        const { form: nextForm, imageFiles: nextFiles } = detailToForm(detail);
        setForm(nextForm);
        setImageFiles(nextFiles);
        captureBaseline({ form: nextForm, imageFiles: nextFiles });
      })
      .catch(() => {
        if (cancelled) return;
        message.error(t("Failed to load product details"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId, enabled, captureBaseline]);

  // 新建打开时记录空表单 baseline
  useEffect(() => {
    if (enabled && !productId) {
      captureBaseline({ form: emptyForm(), imageFiles: [] });
    }
  }, [enabled, productId, captureBaseline]);

  // 关闭/卸载时重置
  useEffect(() => {
    if (!enabled) {
      setForm(emptyForm());
      setImageFiles([]);
      setLinkError(null);
      setSellingPointsStatus("idle");
      clearSellingPointsTimer();
      resetBaseline();
    }
  }, [enabled, clearSellingPointsTimer, resetBaseline]);

  const updateForm = useCallback(
    (patch: Partial<ProductFormState> | ((prev: ProductFormState) => ProductFormState)) => {
      setForm((prev) => (typeof patch === "function" ? patch(prev) : { ...prev, ...patch }));
    },
    [],
  );

  // ===== 图片操作 =====
  /** ClipUpload 受控 onFilesChange */
  const handleFilesChange = useCallback((files: ClipUploadFile[]) => {
    setImageFiles(files as ProductImageFile[]);
  }, []);

  /** 业务侧 uploadFile —— 把图片上传到 /api/video/upload-images，结果塞入 file.response */
  const uploadFile = useCallback(async (file: File, _ctx: ClipUploadContext): Promise<ClipUploadFile> => {
    const [first] = await uploadProductImage(file);
    if (!first?.success || !first.imageUrl) {
      throw new Error(
        first?.rejectionReason ?? t("Image upload failed, please refer to the requirements and re-upload"),
      );
    }
    return {
      uid: nextSlotUid(),
      name: file.name,
      url: first.imageUrl,
      type: file.type,
      response: {
        assetId: first.assetId,
        description: first.description ?? "",
        // 上传时若后端返回 description 则视为已分析；否则需要走 AI Analyze
        aiAnalyzed: !!first.description,
        source: UserProductImageSource.USER,
      } satisfies ProductImageResponse,
    };
  }, []);

  /** 修改某张图片的 caption（同时标记 aiAnalyzed=true） */
  const updateCaption = useCallback((uid: string, caption: string) => {
    setImageFiles((prev) =>
      prev.map((f) => {
        if (f.uid !== uid) return f;
        const meta = getMeta(f);
        return {
          ...f,
          response: {
            ...meta,
            description: caption.slice(0, IMAGE_CAPTION_MAX_LENGTH),
            aiAnalyzed: true,
          } satisfies ProductImageResponse,
        };
      }),
    );
  }, []);

  // ===== 卖点操作 =====
  const setSellingPoints = useCallback(
    (updater: SellingPointItem[] | ((prev: SellingPointItem[]) => SellingPointItem[])) => {
      setForm((prev) => ({
        ...prev,
        sellingPoints: typeof updater === "function" ? updater(prev.sellingPoints) : updater,
      }));
    },
    [],
  );

  const addSellingPoint = useCallback(
    (content: string) => {
      if (!content.trim()) return;
      setSellingPoints((prev) => {
        if (prev.length >= MAX_SELLING_POINTS) return prev;
        return [
          { id: nextSpId(), content: content.trim(), source: UserProductSellingPointSource.USER, checked: true },
          ...prev,
        ];
      });
    },
    [setSellingPoints],
  );

  const removeSellingPoint = useCallback(
    (id: string) => {
      setSellingPoints((prev) => prev.filter((sp) => sp.id !== id));
    },
    [setSellingPoints],
  );

  // ===== AI 生成卖点（链接解析后自动拉取） =====
  const fetchAiSellingPoints = useCallback(
    async (id: string, country: string) => {
      clearSellingPointsTimer();
      const seq = ++sellingPointsSeqRef.current;
      setSellingPointsStatus("loading");

      const tick = async () => {
        try {
          const code = (await generateProductSellingPoints({ id, country } as any)) as unknown as number;
          if (code !== 2) return;
          clearSellingPointsTimer();
          const result: any = await fetchProductSellingPointResult({
            id,
            country,
            attributeKey: "selling_points",
          } as any);
          if (sellingPointsSeqRef.current !== seq) return;
          const items: SellingPointItem[] = (Array.isArray(result) ? result : []).map((item: any) => ({
            id: nextSpId(),
            content: item.keyWord ?? item.content ?? "",
            source: UserProductSellingPointSource.LINK,
            checked: true,
          }));
          setSellingPoints(items);
          setSellingPointsStatus("succeeded");
          captureBaseline({
            form: { ...formRef.current, sellingPoints: items },
            imageFiles: imageFilesRef.current,
          });
        } catch {
          if (sellingPointsSeqRef.current !== seq) return;
          clearSellingPointsTimer();
          setSellingPointsStatus("failed");
        }
      };
      void tick();
      sellingPointsTimerRef.current = setInterval(tick, SELLING_POINTS_POLL_INTERVAL);
    },
    [clearSellingPointsTimer, setSellingPoints, captureBaseline],
  );

  // ===== 收藏商品卖点（Kalodata Saved，直接 GET 现成卖点，无需 POST 触发 / 轮询） =====
  const fetchSavedSellingPoints = useCallback(
    async (id: string, country: string) => {
      clearSellingPointsTimer();
      const seq = ++sellingPointsSeqRef.current;
      setSellingPointsStatus("loading");
      try {
        const result: any = await fetchProductSellingPointResult({
          id,
          country,
          attributeKey: "selling_points",
        } as any);
        if (sellingPointsSeqRef.current !== seq) return;
        const items: SellingPointItem[] = (Array.isArray(result) ? result : []).map((item: any) => ({
          id: nextSpId(),
          content: item.keyWord ?? item.content ?? "",
          source: UserProductSellingPointSource.LINK,
          checked: true,
        }));
        setSellingPoints(items);
        setSellingPointsStatus("succeeded");
      } catch {
        if (sellingPointsSeqRef.current !== seq) return;
        setSellingPointsStatus("failed");
      }
    },
    [clearSellingPointsTimer, setSellingPoints],
  );

  // 切换商品时作废任何在途卖点请求，并复位状态（保证「每次用最新覆盖」）
  const resetSellingPoints = useCallback(() => {
    clearSellingPointsTimer();
    ++sellingPointsSeqRef.current;
    setSellingPointsStatus("idle");
  }, [clearSellingPointsTimer]);

  // ===== 链接解析 =====
  const parseLink = useCallback(
    async (url: string, signal?: AbortSignal): Promise<boolean> => {
      const trimmed = url.trim();
      if (!trimmed) {
        setLinkError(t("Please paste a TikTok or Kalodata product link"));
        return false;
      }
      const finalUrl = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
      setParsingLink(true);
      setLinkError(null);
      try {
        const parsed: any = await fetchParseInfoByLink({ url: finalUrl } as any, { signal });
        if (!parsed || !parsed.product) {
          setLinkError(t("Unable to retrieve product information."));
          return false;
        }
        const linkSource = parsed.linkSource as ProductInfoByLinkResponseLinkSource | undefined;
        const product = parsed.product;
        const validImages: any[] = (parsed.images ?? []).filter((img: any) => img?.success && img.imageUrl);

        const imageSource = linkSource ? linkSourceToImageSource[linkSource] : UserProductImageSource.USER;
        const upsertSource = linkSource
          ? linkSourceToUpsertSource[linkSource]
          : UserProductUpsertRequestSourceType.MANUAL;

        // 直接用 images 列表构建图片槽位（第一张即主图）。
        // coverImageUrl 与 images 是两套不同来源的图，URL 域名不同无法去重，
        // 单独把封面塞进来会多出一张重复图；仅在 images 为空时退回用封面兜底。
        const nextImageFiles: ProductImageFile[] = [];
        for (const img of validImages) {
          if (nextImageFiles.length >= MAX_PRODUCT_IMAGES) break;
          nextImageFiles.push({
            uid: nextSlotUid(),
            name: `image-${img.assetId ?? nextImageFiles.length}`,
            url: img.imageUrl,
            response: {
              assetId: img.assetId,
              description: img.description?.slice(0, IMAGE_CAPTION_MAX_LENGTH) ?? "",
              aiAnalyzed: true, // 链接解析的图片视为已分析
              source: imageSource,
            } satisfies ProductImageResponse,
          });
        }
        if (nextImageFiles.length === 0 && product.coverImageUrl) {
          nextImageFiles.push({
            uid: nextSlotUid(),
            name: "main",
            url: product.coverImageUrl,
            response: {
              assetId: undefined,
              description: "",
              aiAnalyzed: true,
              source: imageSource,
            } satisfies ProductImageResponse,
          });
        }

        const categoryText = [product.priCateName, product.secCateName, product.terCateName]
          .filter(Boolean)
          .join(" > ");

        const nextForm: ProductFormState = {
          sourceType: upsertSource,
          sourceProductId: product.id,
          sourceUrl: finalUrl,
          country: product.country,
          title: product.productTitle ?? "",
          category: categoryText,
          categorySource: UserProductCategorySource.LINK,
          sellingPoints: [],
        };
        setForm(nextForm);
        setImageFiles(nextImageFiles);
        captureBaseline({ form: nextForm, imageFiles: nextImageFiles });

        // 链接解析成功后自动拉卖点
        if (product.id && product.country) {
          void fetchAiSellingPoints(product.id, product.country);
        }

        return true;
      } catch {
        if (signal?.aborted) return false;
        setLinkError(t("Unable to retrieve product information."));
        return false;
      } finally {
        // aborted 时由后到的请求接管 spinner 状态
        if (!signal?.aborted) setParsingLink(false);
      }
    },
    [captureBaseline, fetchAiSellingPoints],
  );

  const inferAiSellingPoints = useCallback(async () => {
    const currentForm = formRef.current;
    const currentFiles = imageFilesRef.current;
    const mainImage = currentFiles[0];
    if (!mainImage?.url || !currentForm.title.trim()) {
      message.warning(t("Please upload main image and enter product name first"));
      return;
    }
    clearSellingPointsTimer();
    const seq = ++sellingPointsSeqRef.current;
    setSellingPointsStatus("loading");
    try {
      const result: any = await getInferSellingPoints({
        imageUrl: mainImage.url,
        title: currentForm.title,
        category: currentForm.category || undefined,
      } as any);
      if (sellingPointsSeqRef.current !== seq) return;
      const items: SellingPointItem[] = (result?.sellingPoints ?? []).map((item: any) => ({
        id: nextSpId(),
        content: item.keyWord ?? item.content ?? "",
        source: UserProductSellingPointSource.AI,
        checked: true,
      }));
      setSellingPoints(items);
      if (result?.category && !currentForm.category) {
        setForm((prev) => ({
          ...prev,
          category: result.category,
          categorySource: UserProductCategorySource.AI,
        }));
      }
      setSellingPointsStatus("succeeded");
    } catch {
      if (sellingPointsSeqRef.current !== seq) return;
      setSellingPointsStatus("failed");
      message.error(t("Failed to generate selling points"));
    }
  }, [clearSellingPointsTimer, setSellingPoints]);

  // ===== AI 分析图片 caption =====
  const needAiAnalyze = useMemo(
    () =>
      imageFiles.some((f) => {
        if (!f.url) return false;
        const meta = getMeta(f);
        return !meta.aiAnalyzed;
      }),
    [imageFiles],
  );

  const aiAnalyze = useCallback(async () => {
    const currentForm = formRef.current;
    const need = imageFilesRef.current.filter((f) => f.url && !getMeta(f).aiAnalyzed);
    if (need.length === 0) return;
    if (!currentForm.title.trim() || !currentForm.category.trim()) {
      message.warning(t("Please fill product name and category before AI analyze"));
      return;
    }
    setAiAnalyzing(true);
    try {
      const results: any[] = await fetchAiAnalyzeImage({
        title: currentForm.title,
        category: currentForm.category,
        imageUrls: need.map((f) => f.url!),
      } as any);
      const ok = (Array.isArray(results) ? results : []).filter((r) => r.success);
      setImageFiles((prev) =>
        prev.map((f) => {
          const target = ok.find((r: any) => r.imageUrl === f.url);
          if (!target) return f;
          const meta = getMeta(f);
          return {
            ...f,
            response: {
              ...meta,
              description: target.description?.slice(0, IMAGE_CAPTION_MAX_LENGTH) ?? meta.description,
              aiAnalyzed: true,
            } satisfies ProductImageResponse,
          };
        }),
      );
    } catch (err: any) {
      showFallbackErrorToast(err, t("Failed to analyze images"));
    } finally {
      setAiAnalyzing(false);
    }
  }, []);

  // ===== 提交 =====
  const submit = useCallback(async (): Promise<UserProductDetail | null> => {
    const currentForm = formRef.current;
    const currentFiles = imageFilesRef.current;

    if (currentFiles.length === 0 || !currentFiles[0].url) {
      message.error(t("Please upload a main product image"));
      return null;
    }
    if (!currentForm.title.trim()) {
      message.error(t("Please enter product name"));
      return null;
    }
    if (!currentForm.category.trim()) {
      message.error(t("Please enter product category"));
      return null;
    }

    const [mainFile, ...otherFiles] = currentFiles;
    const mainMeta = getMeta(mainFile);
    const captions = currentFiles.map((f) => getMeta(f).description ?? "");

    const categoryParts = currentForm.category.split(">").map((s) => s.trim());

    const body = {
      sourceType: currentForm.sourceType,
      sourceProductId: currentForm.sourceProductId,
      sourceUrl: currentForm.sourceUrl,
      country: currentForm.country,
      title: currentForm.title.trim(),
      mainImage: {
        url: mainFile.url!,
        assetId: mainMeta.assetId ?? null,
        source: mainMeta.source,
      },
      images: otherFiles
        .filter((f) => !!f.url)
        .map((f) => {
          const meta = getMeta(f);
          return {
            url: f.url!,
            assetId: meta.assetId ?? null,
            source: meta.source,
          };
        }),
      category: {
        priCateName: categoryParts[0] || undefined,
        secCateName: categoryParts[1] || undefined,
        terCateName: categoryParts[2] || undefined,
        source: currentForm.categorySource ?? UserProductCategorySource.USER,
      },
      sellingPoints: currentForm.sellingPoints
        .filter((sp) => sp.checked && sp.content.trim())
        .map((sp) => ({
          content: sp.content.trim(),
          source: sp.source ?? UserProductSellingPointSource.USER,
        })),
      metadata: {
        imageCaptions: captions,
      },
    };

    setSubmitting(true);
    try {
      let detail: UserProductDetail | null;
      if (productId) {
        detail = (await updateUserProduct({ query: { productId }, body } as any)) as UserProductDetail;
      } else {
        detail = (await createUserProduct(body as any)) as UserProductDetail;
      }
      message.success(productId ? t("Product updated") : t("Product created"));
      onSuccess?.(detail);
      return detail;
    } catch (err: any) {
      showFallbackErrorToast(err, t("Failed to save product"));
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [productId, onSuccess]);

  return {
    form,
    updateForm,
    setForm,
    imageFiles,
    setImageFiles,
    handleFilesChange,
    uploadFile,
    updateCaption,
    loading,
    submitting,
    parsingLink,
    linkError,
    setLinkError,
    sellingPointsStatus,
    aiAnalyzing,
    needAiAnalyze,

    setSellingPoints,
    addSellingPoint,
    removeSellingPoint,

    parseLink,
    fetchAiSellingPoints,
    fetchSavedSellingPoints,
    resetSellingPoints,
    inferAiSellingPoints,
    aiAnalyze,
    submit,

    isDirty,
    captureBaseline,
  };
};
